import { inject } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ClientLogger } from 'bernie-client';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { UitkDatePickerTriggeredSelector } from 'uitk-react-date-picker';

import { DateField } from './date-field';
import { FormFieldBinding } from './form-field-binding';
import { NewDateField } from './new-date-field';
import { SearchFormFieldProps } from 'components/common/search-form';

export interface DateRangeState {
  endDateFocus?: () => void;
  startDateFocus?: () => void;
}

export type LocalizedDateRateProps = LocalizedComponentProps & SearchFormFieldProps;

export interface DateRangeProps extends LocalizedDateRateProps {
  currentHotel?: CurrentHotelStore;
  fetchDatePickerRates?: () => Promise<object>;
}

const logger = ClientLogger.getLoggerWithIdentifier('pwa.component.searchform');

@withLocalization
@FormFieldBinding
@inject('currentHotel')
export class DateRange extends React.Component<DateRangeProps, DateRangeState> {
  constructor(props) {
    super(props);

    this.state = {
      endDateFocus: undefined,
      startDateFocus: undefined
    };

    this.handleChange = this.handleChange.bind(this);
    this.registerFocusMethod = this.registerFocusMethod.bind(this);
    this.validate = this.validate.bind(this);

    const { registerValidator } = props;

    registerValidator(() => this.validate('startDate', true));
    registerValidator(() => this.validate('endDate', true));
  }

  private handleChange(event) {
    const { updateModel } = this.props;

    if (typeof updateModel === 'function') {
      updateModel(event);
    }
  }

  private logDateRangeError(startDateError, endDateError, startDate, endDate) {
    logger.logEvent(
      new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM_DATE_RANGE_ERROR'),
      `${startDateError} | ${endDateError}`,
      `startDate=${startDate} endDate=${endDate}`
    );
  }

  public render() {
    const { l10n, model, wrapperClassNames, currentHotel, autoSubmit, isSubmitted, fetchDatePickerRates, callToAction } = this.props;
    const { formatText } = l10n;
    const startDate = model && model.startDate;
    const endDate = model && model.endDate;
    const buttonText =
      autoSubmit || (currentHotel.searchCriteria && currentHotel.searchCriteria.isDateless)
        ? callToAction || formatText('hotels.search.form.submit.label')
        : formatText('hotels.roomPicker.doneButton.label');
    const wrapperClasses = wrapperClassNames || 'uitk-cell s-cell-1-2 m-cell-1-4 l-cell-1-6 xl-cell-1-6';
    const startDateError = this.validate('startDate', isSubmitted);
    const endDateError = this.validate('endDate', isSubmitted);

    const commonDateFieldProps = {
      startDate,
      endDate,
      onChange: this.handleChange,
      placeholder: formatText('hotels.search.form.toDate.placeholder'),
      numDaysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
      midnightBookingOffsetHours: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
      buttonText,
      registerFocusMethod: this.registerFocusMethod
    };

    if (logger && typeof logger.logEvent === 'function' && (startDateError || endDateError)) {
      this.logDateRangeError(startDateError, endDateError, startDate, endDate);
    }

    const routerLinkWrapper = children => <>{children}</>;

    const dateFieldProps = {
      dateFieldWrapperClasses: wrapperClasses,
      endId: 'hotels-check-out',
      endInvalid: endDateError && endDateError.text,
      endLabel: formatText('hotels.search.form.toDateHyphen.label'),
      endName: 'endDate',
      endPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
      onChange: commonDateFieldProps.onChange,
      routerLinkWrapper,
      startId: 'hotels-check-in',
      startInvalid: startDateError && startDateError.text,
      startLabel: formatText('hotels.search.form.fromDateHyphen.label'),
      startName: 'startDate',
      startPlaceholder: formatText('hotels.search.form.toDate.placeholder')
    };

    const rfrr = {
      end: ':ChangeDatesCheckOut',
      start: ':ChangeDatesCheckIn'
    };

    if (currentHotel.searchCriteria.isDateless) {
      return (
        <AbacusExperiment name="PDP_Dateless_new_date_picker">
          <AbacusControl>
            <>
              <div className={wrapperClasses}>
                <DateField
                  dateType={UitkDatePickerTriggeredSelector.START}
                  id="hotels-check-in"
                  name="startDate"
                  label={formatText('hotels.search.form.fromDateHyphen.label')}
                  invalid={startDateError && startDateError.text}
                  rfrr=":ChangeDatesCheckIn"
                  isDateless={currentHotel.searchCriteria.isDateless}
                  {...commonDateFieldProps}
                />
              </div>
              <div className={wrapperClasses}>
                <DateField
                  dateType={UitkDatePickerTriggeredSelector.END}
                  id="hotels-check-out"
                  name="endDate"
                  label={formatText('hotels.search.form.toDateHyphen.label')}
                  invalid={endDateError && endDateError.text}
                  rfrr=":ChangeDatesCheckOut"
                  isDateless={currentHotel.searchCriteria.isDateless}
                  {...commonDateFieldProps}
                />
              </div>
            </>
          </AbacusControl>
          <AbacusVariant bucket={1}>
            <NewDateField
              rfrr={rfrr}
              dateFieldProps={dateFieldProps}
              isDateless={currentHotel.searchCriteria.isDateless}
              fetchDatePickerRates={fetchDatePickerRates}
              {...commonDateFieldProps}
            />
          </AbacusVariant>
        </AbacusExperiment>
      );
    }

    return (
      <NewDateField
        rfrr={rfrr}
        dateFieldProps={dateFieldProps}
        isDateless={currentHotel.searchCriteria.isDateless}
        fetchDatePickerRates={fetchDatePickerRates}
        {...commonDateFieldProps}
      />
    );
  }

  private registerFocusMethod(fieldName: string, focusMethod: () => void) {
    this.setState({ [`${fieldName}Focus`]: focusMethod });
  }

  private validate(key, isSubmitted) {
    const { l10n, model, showErrorValidationOnSubmit } = this.props;
    const { startDateFocus, endDateFocus } = this.state;

    const { formatText } = l10n;

    const { endDate, startDate } = model;

    if (key === 'startDate') {
      const start = startDate && new Date(startDate);
      const message =
        isSubmitted && showErrorValidationOnSubmit
          ? 'hotels.search.validation.invalid.dateless.text'
          : 'hotels.search.validation.invalidFromDate.text';

      if (!this.isValidDate(start) && (!model.isDateless || (isSubmitted && showErrorValidationOnSubmit))) {
        return {
          text: formatText(message),
          focus: startDateFocus
        };
      }
    }

    if (key === 'endDate') {
      const start = startDate && new Date(startDate);
      const end = endDate && new Date(endDate);
      const message =
        isSubmitted && showErrorValidationOnSubmit
          ? 'hotels.search.validation.invalid.dateless.text'
          : 'hotels.search.validation.invalidToDate.text';

      if (!this.isValidDate(end) && (!model.isDateless || (isSubmitted && showErrorValidationOnSubmit))) {
        return {
          text: formatText(message),
          focus: endDateFocus
        };
      }

      if (this.isValidDate(start)) {
        const maxEndDate = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 28);

        if (end < start) {
          return {
            text: formatText('hotels.search.validation.invalidDateRange.text'),
            focus: endDateFocus
          };
        }

        if (end > maxEndDate) {
          return {
            text: formatText('hotels.search.validation.invalidDateDateRangeLength.text'),
            focus: endDateFocus
          };
        }
      }
    }
  }

  private isValidDate(date: Date) {
    return date && date.toString() !== 'Invalid Date';
  }
}
